/**
 * Untitled UI toggle, ported from the dashboard's `components/ui/toggle`.
 * A react-aria `Switch`: a real checkbox with role="switch" for screen
 * readers and tests.
 */
import type { ReactNode } from "react";
import type { SwitchProps as AriaSwitchProps } from "react-aria-components";

import { Switch as AriaSwitch } from "react-aria-components";

import { cx } from "@/utils/cx";

interface ToggleBaseProps {
	className?: string;
	isDisabled?: boolean;
	isFocusVisible?: boolean;
	isHovered?: boolean;
	isSelected?: boolean;
	size?: "md" | "sm";
	slim?: boolean;
}

export const ToggleBase = ({
	className,
	isDisabled,
	isFocusVisible,
	isHovered,
	isSelected,
	size = "sm",
	slim,
}: ToggleBaseProps) => {
	const styles = {
		default: {
			md: {
				root: "h-6 w-11 p-0.5",
				switch: cx("size-5", isSelected && "translate-x-5"),
			},
			sm: {
				root: "h-5 w-9 p-0.5",
				switch: cx("size-4", isSelected && "translate-x-4"),
			},
		},
		slim: {
			md: {
				root: "h-5 w-10",
				switch: cx("size-5", isSelected && "translate-x-5"),
			},
			sm: {
				root: "h-4 w-8",
				switch: cx("size-4", isSelected && "translate-x-4"),
			},
		},
	};

	const classes = slim ? styles.slim[size] : styles.default[size];

	return (
		<div
			className={cx(
				"cursor-pointer rounded-full bg-tertiary outline-focus-ring transition duration-150 ease-linear",
				isSelected && "bg-brand-solid",
				isSelected && isHovered && "bg-brand-solid_hover",
				isDisabled && "cursor-not-allowed opacity-50",
				isFocusVisible && "outline-2 outline-offset-2",

				slim && "ring-1 ring-secondary ring-inset",
				slim && isSelected && "ring-transparent",
				classes.root,
				className,
			)}
		>
			<div
				className={cx(
					"rounded-full bg-fg-white shadow-sm",

					slim && "shadow-xs",
					slim && "border border-toggle-border",
					slim && isSelected && "border-toggle-slim-border_pressed",
					slim &&
						isSelected &&
						isHovered &&
						"border-toggle-slim-border_pressed-hover",

					classes.switch,
				)}
				style={{
					transition:
						"transform 0.15s ease-in-out, translate 0.15s ease-in-out, border-color 0.1s linear, background-color 0.1s linear",
				}}
			/>
		</div>
	);
};

interface ToggleProps extends AriaSwitchProps {
	hint?: ReactNode;
	label?: string;
	size?: "md" | "sm";
	slim?: boolean;
}

export const Toggle = ({
	className,
	hint,
	label,
	size = "sm",
	slim,
	...ariaSwitchProps
}: ToggleProps) => {
	const sizes = {
		md: {
			hint: "text-md",
			label: "text-md font-medium",
			root: "gap-3",
			textWrapper: "gap-0.5",
		},
		sm: {
			hint: "text-sm",
			label: "text-sm font-medium",
			root: "gap-2",
			textWrapper: "",
		},
	};

	return (
		<AriaSwitch
			{...ariaSwitchProps}
			className={(renderProps) =>
				cx(
					"flex w-max items-start",
					renderProps.isDisabled && "cursor-not-allowed",
					sizes[size].root,
					typeof className === "function" ? className(renderProps) : className,
				)
			}
		>
			{({ isDisabled, isFocusVisible, isHovered, isSelected }) => (
				<>
					<ToggleBase
						className={slim ? "mt-0.5" : ""}
						isDisabled={isDisabled}
						isFocusVisible={isFocusVisible}
						isHovered={isHovered}
						isSelected={isSelected}
						size={size}
						slim={slim}
					/>

					{(label || hint) && (
						<div className={cx("flex flex-col", sizes[size].textWrapper)}>
							{label && (
								<p
									className={cx(
										"text-secondary select-none",
										sizes[size].label,
									)}
								>
									{label}
								</p>
							)}
							{hint && (
								<span
									className={cx("text-tertiary", sizes[size].hint)}
									onClick={(event) => event.stopPropagation()}
								>
									{hint}
								</span>
							)}
						</div>
					)}
				</>
			)}
		</AriaSwitch>
	);
};
